import { Box, Divider, HStack, Link, Text, VStack } from "@chakra-ui/react";
import React, { useState } from "react";
import { isNumber } from "lodash-es";
import { CherryPayApi } from "../../api/models";
import { CheckCircleFillIcon, WarningFillIcon } from "../../styles/icons";
import DateUtil from "../../util/DateUtil";
import { isEmptyStr } from "../../util/StringUtil";
import { formatStopSendingDuration } from "../../forms/Notifications/NotificationUtils";
import { EligibleMembersDrawer } from "../NotificationScheduleDetails/EligibleMembersDrawer";

interface FormNotificationSummaryProps {
  notification: CherryPayApi.CherryPassNotificationScheduleDetail;
  memberCount?: number | null;
  businessId: string;
  geoLocations: CherryPayApi.GeoLocation[];
  locationMessageOverrides: Record<string, string>;
}

const SummaryRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <HStack alignItems="flex-start" spacing={10} w="100%">
    <Text
      fontWeight="medium"
      color="gray.600"
      w="220px"
      minW="220px"
      flexShrink={0}
    >
      {label}
    </Text>
    <Box flex="1" minW={0}>
      {children}
    </Box>
  </HStack>
);

const SuccessStatus = ({ label }: { label: string }) => (
  <HStack spacing={2}>
    <CheckCircleFillIcon color="riskRating.safe.icon" boxSize={4} />
    <Text fontSize="sm" color="riskRating.safe.icon" fontWeight="medium">
      {label}
    </Text>
  </HStack>
);

export const FormNotificationSummary: React.FC<FormNotificationSummaryProps> =
  ({
    notification,
    memberCount,
    businessId,
    geoLocations,
    locationMessageOverrides,
  }) => {
    const [isEligibleMembersDrawerOpen, setIsEligibleMembersDrawerOpen] =
      useState(false);

    const isMessageConfigured = !isEmptyStr(notification.PassUpdateMessage);
    const hasGeoLocations = geoLocations.length > 0;
    const overrideCount = geoLocations.reduce((count, location) => {
      const overrideKey = location.Meta?.Id;
      if (!overrideKey) return count;
      return !isEmptyStr(locationMessageOverrides[overrideKey])
        ? count + 1
        : count;
    }, 0);

    const isScheduleConfigured = !!notification.LocalScheduleSendDateTime;

    const stopSendingText = (() => {
      if (
        !notification.MaximumLocalScheduleSendDateTime ||
        !notification.LocalScheduleSendDateTime
      ) {
        return "Deliver Anytime";
      }

      const start = new Date(notification.LocalScheduleSendDateTime);
      const end = new Date(notification.MaximumLocalScheduleSendDateTime);
      const diffMinutes = Math.ceil((end.getTime() - start.getTime()) / 60000);
      return formatStopSendingDuration(diffMinutes);
    })();

    const scheduleDateTime = notification.LocalScheduleSendDateTime
      ? DateUtil.getFormattedNotificationSummaryDateTime(
          notification.LocalScheduleSendDateTime
        )
      : "";

    return (
      <Box
        background="white"
        borderRadius="lg"
        boxShadow="md"
        overflow="hidden"
        p={6}
      >
        <VStack align="stretch" spacing={5} w="100%">
          <Text fontSize="xl" fontWeight="700" color="gray.700">
            Notification Summary
          </Text>
          <Divider borderColor="cherryBorder.100" />

          <SummaryRow label="Push Notification Message">
            {isMessageConfigured ? (
              <SuccessStatus label="Configured" />
            ) : (
              <Text fontSize="sm" color="gray.700">
                No message
              </Text>
            )}
          </SummaryRow>

          {hasGeoLocations && (
            <SummaryRow label="Geo-Fence Messages">
              {overrideCount > 0 ? (
                <SuccessStatus
                  label={`${overrideCount} of ${geoLocations.length} Overridden`}
                />
              ) : (
                <Text fontSize="sm" color="gray.700">
                  {`0 of ${geoLocations.length} Overridden`}
                </Text>
              )}
            </SummaryRow>
          )}

          <SummaryRow label="Total Eligible Members">
            <VStack align="start" spacing={1}>
              <Text fontSize="sm" color="gray.700">
                {isNumber(memberCount)
                  ? `${memberCount.toLocaleString()} ${
                      memberCount === 1 ? "Member" : "Members"
                    }`
                  : "Loading..."}
              </Text>
              {isNumber(memberCount) && memberCount > 0 && (
                <Link
                  color="cherryUi.600"
                  onClick={() => setIsEligibleMembersDrawerOpen(true)}
                  cursor="pointer"
                  fontSize="sm"
                >
                  {`View Eligible Members >`}
                </Link>
              )}
            </VStack>
          </SummaryRow>

          <SummaryRow label="Notification Schedule">
            {isScheduleConfigured ? (
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" color="gray.700" fontWeight="medium">
                  {scheduleDateTime}
                </Text>
                {notification.TimeZoneId && (
                  <Text fontSize="sm" color="gray.600">
                    {DateUtil.getTimezoneDisplayName(notification.TimeZoneId)}
                  </Text>
                )}
                <Text fontSize="sm" color="gray.700" pt={1}>
                  Your push notification will be sent to selected members at the
                  scheduled send date and time, delivering directly to their
                  digital wallet.
                </Text>
              </VStack>
            ) : (
              <HStack
                bg="riskRating.reviewRequired.bg"
                borderRadius="full"
                px={3}
                py={1}
                spacing={2}
                w="fit-content"
              >
                <WarningFillIcon
                  color="riskRating.reviewRequired.icon"
                  boxSize={4}
                />
                <Text
                  fontSize="sm"
                  color="riskRating.reviewRequired.icon"
                  fontWeight="medium"
                >
                  Date & Time Required
                </Text>
              </HStack>
            )}
          </SummaryRow>

          {isScheduleConfigured && (
            <SummaryRow label="Stop Sending After">
              <Text fontSize="sm" color="gray.700">
                {stopSendingText}
              </Text>
            </SummaryRow>
          )}
        </VStack>

        {isEligibleMembersDrawerOpen && (
          <EligibleMembersDrawer
            isOpen={isEligibleMembersDrawerOpen}
            onClose={() => setIsEligibleMembersDrawerOpen(false)}
            businessId={businessId}
            memberFilterQuery={notification.MemberFilterQuery}
            totalMembers={isNumber(memberCount) ? memberCount.toString() : "0"}
          />
        )}
      </Box>
    );
  };
